/** 复盘统计：雷达图（双方评分）+ 参与/告警/批注/覆盖率等指标。 */
import { useMemo } from 'react';
import { sideLabel, type MotionPackage, type Side } from '@zongheng/core-schema';
import { useArena } from '../../state/ArenaContext.tsx';
import { buildEvidenceCoverage } from '../../export/report.ts';
import { useCoachScores } from './scoreStore.ts';
import { RadarChart } from './RadarChart.tsx';

export function ReviewAnalyticsPanel({ motion }: { motion: MotionPackage }) {
  const { state } = useArena();
  const scores = useCoachScores();
  const rubric = motion.coachRubric;
  const debaters = state.participants.filter((participant) => participant.role === 'DEBATER');
  const coverage = buildEvidenceCoverage(motion, state.manualEvidenceChecks);

  const averages = useMemo(() => {
    const result: Record<Side, number[]> = { AFF: [], NEG: [] };
    for (const side of ['AFF', 'NEG'] as const) {
      const members = debaters.filter((participant) => participant.side === side);
      result[side] = rubric.dimensions.map((dimension) => {
        const values = members
          .map((member) => scores[member.id]?.[dimension.id])
          .filter((value): value is number => typeof value === 'number');
        if (values.length === 0) return 0;
        return values.reduce((sum, value) => sum + value, 0) / values.length;
      });
    }
    return result;
  }, [debaters, rubric.dimensions, scores]);

  const hasScores = [...averages.AFF, ...averages.NEG].some((value) => value > 0);
  const stageActivity = useMemo(() => {
    const counts = new Map<number, number>();
    for (const event of state.eventLog) {
      if (!event.stageIndex) continue;
      counts.set(event.stageIndex, (counts.get(event.stageIndex) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0] - b[0]).slice(-8);
  }, [state.eventLog]);
  const maxActivity = Math.max(1, ...stageActivity.map(([, count]) => count));

  const stats = [
    { label: '参与辩手', value: `${debaters.length}`, tone: 'text-slate-100' },
    { label: '已完成环节', value: `${state.completedStages.length}/16`, tone: 'text-slate-100' },
    { label: '断言告警', value: `${state.alerts.length}`, tone: state.alerts.length ? 'text-[#FDE68A]' : 'text-slate-400' },
    { label: '教师批注', value: `${state.annotations.length}`, tone: 'text-slate-100' },
    {
      label: '必打覆盖',
      value: `${coverage.AFF.hitCount + coverage.NEG.hitCount}/${coverage.AFF.requiredCount + coverage.NEG.requiredCount}`,
      tone: 'text-[#BAE6FD]',
    },
  ];

  return (
    <section className="lens" data-testid="review-analytics">
      <header className="flex flex-wrap items-start justify-between gap-2 border-b border-white/5 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold tracking-wide text-slate-100">参与与能力雷达</h2>
          <p className="mt-0.5 text-[11px] text-slate-400">
            评分维度来自 Motion 的 coachRubric；左右阵营取各自辩手的已录入均分。
          </p>
        </div>
        <span className="pill">{hasScores ? '已录入评分' : '尚未评分'}</span>
      </header>
      <div className="grid gap-4 p-3 md:grid-cols-[240px_minmax(0,1fr)]">
        <div className="flex flex-col items-center justify-center">
          <RadarChart
            labels={rubric.dimensions.map((dimension) => dimension.label)}
            series={[
              { label: '正方', color: '#7DD3FC', values: averages.AFF },
              { label: '反方', color: '#FDA4AF', values: averages.NEG },
            ]}
          />
          <div className="mt-1 flex gap-4 text-[10px]">
            <span className="flex items-center gap-1 text-[#BAE6FD]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7DD3FC]" />
              {sideLabel('AFF')}
            </span>
            <span className="flex items-center gap-1 text-[#FECDD3]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FDA4AF]" />
              {sideLabel('NEG')}
            </span>
          </div>
        </div>
        <div className="min-w-0 space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-lg bg-white/[0.025] px-2 py-2">
                <div className="text-[9px] uppercase tracking-wider text-slate-500">{stat.label}</div>
                <div className={`mono mt-1 text-[17px] font-semibold ${stat.tone}`}>{stat.value}</div>
              </div>
            ))}
          </div>
          <div>
            <div className="micro-label mb-1">环节事件活跃度</div>
            {stageActivity.length === 0 ? (
              <p className="text-[10px] text-slate-500">开赛后显示每个环节的事件密度。</p>
            ) : (
              <div className="flex h-16 items-end gap-1.5">
                {stageActivity.map(([stage, count]) => (
                  <div key={stage} className="flex min-w-0 flex-1 flex-col items-center gap-1" title={`环节 ${stage}：${count} 条事件`}>
                    <div
                      className="w-full rounded-t bg-gradient-to-t from-[#7DD3FC]/20 to-[#E2E8F0]/70"
                      style={{ height: `${Math.max(4, (count / maxActivity) * 52)}px` }}
                    />
                    <span className="mono text-[8px] text-slate-600">{stage}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
